import React from "react"
import { createPortal } from "react-dom"
import { Phone, PhoneSlash, Headphones, MapPin, Buildings, Clock } from "@phosphor-icons/react"
import { cn } from "../../lib/utils"
import { AgentAvatar } from "./AgentAvatar"
import { useCallStore } from "../../stores"
import { acceptCall, rejectCall } from "../../lib/twilio"

// Format phone number for display
function formatPhone(phone: string): string {
  if (!phone) return ""
  const cleaned = phone.replace(/\D/g, "")
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  if (cleaned.length === 11 && cleaned[0] === "1") {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  return phone
}

/**
 * Full-screen incoming call overlay rendered via portal.
 * Mounted at the Sidebar level (always mounted) so it is never affected
 * by AgentCommandCenter's conditional rendering or React StrictMode
 * unmount/remount cycles that would destroy and recreate the portal.
 */
export function IncomingCallOverlay() {
  const { incomingCall } = useCallStore()

  if (!incomingCall) return null

  const isInternalTransfer = incomingCall.number === "Internal Transfer"
  // Longest-idle routing populates call_queue — render queue-style UI instead
  // of the transfer-style UI. Accept/reject still use the Twilio Device flow.
  const isQueueCall = !!incomingCall.call_queue

  const getCallerInitials = () => {
    if (!incomingCall.name) return "?"
    const parts = incomingCall.name.split(" ")
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return incomingCall.name.slice(0, 2).toUpperCase()
  }

  if (isQueueCall) {
    return createPortal(
      <div className="fixed inset-0 z-[99998] flex items-center justify-center pointer-events-auto">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

        {/* Modal */}
        <div className="relative queue-modal-container animate-queue-appear">
          {/* Pulsing border effect */}
          <div className="absolute inset-0 rounded-2xl queue-pulse-border" />

          <div className="relative bg-sidebar-dark rounded-2xl overflow-hidden shadow-2xl min-w-[320px]">
            {/* Header */}
            <div
              className={cn(
                "px-6 py-4",
                incomingCall.queue_entry_color
                  ? ""
                  : "bg-gradient-to-r from-primary to-primary/80"
              )}
              style={
                incomingCall.queue_entry_color
                  ? { background: incomingCall.queue_entry_color }
                  : undefined
              }
            >
              <h3 className="text-lg font-bold text-white text-center">
                {incomingCall.queue_entry_title || "Incoming Queue Call"}
              </h3>
            </div>

            {/* Body */}
            <div className="px-6 py-5 bg-white">
              <h2 className="text-xl font-bold text-gray-900 text-center">
                {incomingCall.name
                  ? incomingCall.name.length > 25
                    ? incomingCall.name.slice(0, 25) + "..."
                    : incomingCall.name
                  : "Unknown Caller"}
              </h2>
              <p className="mt-1 text-sm font-semibold text-gray-600 text-center">
                {formatPhone(incomingCall.number)}
              </p>
              <div className="mt-1 flex items-center justify-center gap-1 text-sm text-gray-500">
                <MapPin className="h-3 w-3" />
                <span>{incomingCall.address || "Unknown Location"}</span>
              </div>
            </div>

            {/* Info */}
            <div className="px-6 py-3 bg-gray-100 border-t border-gray-200">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Buildings className="h-3.5 w-3.5" />
                <span className="font-medium">Division:</span>
                <span>{incomingCall.division_name || "—"}</span>
              </div>
              <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
                <Clock className="h-3.5 w-3.5" />
                <span className="font-medium">Type:</span>
                <span>On Hold</span>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-white border-t border-gray-200">
              <div className="flex gap-3">
                <button
                  onClick={rejectCall}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 py-3 rounded-xl",
                    "bg-danger hover:bg-danger/90 transition-colors",
                    "text-sm font-bold text-white shadow-lg",
                    "hover:shadow-danger/30 hover:shadow-xl"
                  )}
                >
                  <PhoneSlash className="h-4 w-4" />
                  <span>Decline</span>
                </button>
                <button
                  onClick={acceptCall}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 py-3 rounded-xl",
                    "bg-success hover:bg-success/90 transition-colors",
                    "text-sm font-bold text-white shadow-lg",
                    "hover:shadow-success/30 hover:shadow-xl"
                  )}
                >
                  <Phone className="h-4 w-4" />
                  <span>Accept</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    )
  }

  return createPortal(
    <div className="fixed inset-0 z-[99998] flex items-center justify-center pointer-events-auto">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" />

      {/* Modal */}
      <div className="relative incoming-call-container animate-queue-appear">
        {/* Pulsing border glow — same pattern as QueueModal */}
        <div className="absolute inset-0 rounded-2xl incoming-pulse-border" />

        <div className="relative bg-[#0d1f2d] rounded-2xl overflow-hidden shadow-2xl min-w-[320px]">
          <div className="px-6 py-6 flex flex-col items-center">
            {/* Header */}
            <div className="flex items-center gap-2 mb-4">
              <Headphones className="h-4 w-4 text-white/80" />
              <span className="text-xs font-semibold uppercase tracking-wider text-white/80">
                {isInternalTransfer ? "Internal Transfer" : "Incoming Call"}
              </span>
            </div>

            {/* Caller Avatar */}
            <AgentAvatar initials={getCallerInitials()} status="ringing" size="md" />

            {/* Caller Info */}
            <p className="mt-3 text-lg font-bold text-white">
              {incomingCall.name || "Unknown Caller"}
            </p>
            <p className="text-sm font-medium text-white/80">
              {formatPhone(incomingCall.number)}
            </p>
            {incomingCall.address && !isInternalTransfer && (
              <p className="text-xs text-white/60">{incomingCall.address}</p>
            )}

            {/* Action buttons */}
            <div className="mt-5 flex gap-3 w-full">
              <button
                onClick={rejectCall}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 py-3 rounded-full",
                  "bg-danger hover:bg-danger/90 transition-colors",
                  "text-sm font-bold uppercase text-white shadow-lg",
                  "hover:shadow-danger/30 hover:shadow-xl"
                )}
              >
                <PhoneSlash className="h-4 w-4" />
                <span>Decline</span>
              </button>
              <button
                onClick={acceptCall}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 py-3 rounded-full",
                  "bg-success hover:bg-success/90 transition-colors",
                  "text-sm font-bold uppercase text-white shadow-lg",
                  "hover:shadow-success/30 hover:shadow-xl"
                )}
              >
                <Phone className="h-4 w-4" />
                <span>Accept</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
